import axios from "axios";
import DOMPurify from "dompurify";
import { processImageFileForTicket, TICKET_LOGO_MAX_WIDTH } from "./ticketImage";

/** Base HTTP sin /api. Si no hay env (Docker + proxy nginx), usa el origen del navegador (:3002 → /api → backend). */
function resolveBackendBaseUrl() {
    const raw = process.env.REACT_APP_BACKEND_URL;
    if (raw != null && String(raw).trim() !== "") {
        return String(raw).trim().replace(/\/$/, "");
    }
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return "http://localhost:8001";
}

export const API = `${resolveBackendBaseUrl()}/api`;

// withCredentials sends/receives the httpOnly access_token cookie. Tokens are
// never accessible from JS — defense against XSS exfiltration.
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.response.use(
    (res) => res,
    (err) => {
        const detail = err?.response?.data?.detail;
        if (
            err?.response?.status === 401 &&
            typeof detail === "string" &&
            detail.toLowerCase().includes("otro equipo")
        ) {
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("lozmar:session-replaced"));
                if (
                    window.location.pathname.startsWith("/admin") &&
                    !window.location.pathname.includes("/admin/login")
                ) {
                    window.location.assign("/admin/login?reason=session");
                }
            }
        }
        return Promise.reject(err);
    },
);

export function formatApiErrorDetail(detail, err) {
    if (detail == null) {
        if (err?.response?.status === 401) return "Sesión expirada. Vuelve a iniciar sesión en Admin.";
        if (err?.response?.status === 413) return "Archivo demasiado grande para el servidor.";
        if (err?.response?.status) return `Error del servidor (${err.response.status}). Revisa los logs del backend.`;
        if (err?.code === "ERR_NETWORK") return "No se pudo conectar con el servidor. Comprueba que el backend esté en línea.";
        return "Algo salió mal. Intenta de nuevo.";
    }
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

/** Extrae mensaje de error desde respuesta axios. */
export function formatApiError(err) {
    return formatApiErrorDetail(err?.response?.data?.detail, err);
}

export function formatPrice(n) {
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
    }).format(Number(n || 0));
}

export function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/** Redimensiona logo para ticket térmico: PNG blanco/negro, ancho máx. 384 px. */
export function resizeImageForTicket(file, maxWidth = TICKET_LOGO_MAX_WIDTH) {
    return processImageFileForTicket(file, maxWidth);
}

/** Imagen del banner principal (color, JPEG). */
export function resizeImageForHero(file, maxWidth = 1200) {
    return new Promise((resolve, reject) => {
        if (!file?.type?.startsWith("image/")) {
            reject(new Error("Selecciona un archivo de imagen"));
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
                const scale = Math.min(1, maxWidth / img.width);
                const w = Math.max(1, Math.round(img.width * scale));
                const h = Math.max(1, Math.round(img.height * scale));
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL("image/jpeg", 0.88));
            };
            img.onerror = () => reject(new Error("No se pudo leer la imagen"));
            img.src = ev.target.result;
        };
        reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
        reader.readAsDataURL(file);
    });
}

const SANITIZE_OPTS = {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "a", "span"],
    ALLOWED_ATTR: ["href", "title", "target", "rel"],
};

export function sanitizeHtml(html) {
    return DOMPurify.sanitize(html || "", SANITIZE_OPTS);
}
